import { create } from 'zustand';
import type { UserContext } from '@vanialabs/core-permissions';

/**
 * Feature-layer store for the representative-admin app's UserContext(s).
 *
 * Deliberately separate from @vanialabs/core-auth's useAuthStore:
 *  - core-auth must stay infrastructure-only and must not know about
 *    UserContext/core-permissions.
 *  - core-permissions must stay a type-only contract, not a state owner.
 *  - This store is the adapter layer that is allowed to know about both.
 *
 * `contexts` is populated from the login response via toUserContexts()
 * (see ../api/userContext.mapper.ts) and is not persisted to localStorage
 * this phase — it is rebuilt fresh on every login. Restoring it across a
 * page reload (in authInitializer.ts) is out of scope for this change.
 */
export interface UserContextState {
  contexts: UserContext[];

  setContexts: (contexts: UserContext[]) => void;
  clearContexts: () => void;

  /**
   * Looks up the context matching `activeContextId` (e.g.
   * AuthSession.activeContextId from core-auth). Returns undefined if not
   * found or if activeContextId is undefined — never throws.
   */
  getActiveContext: (activeContextId: string | undefined) => UserContext | undefined;
}

export const useUserContextStore = create<UserContextState>((set, get) => ({
  contexts: [],

  setContexts: (contexts) => {
    set({ contexts });
  },

  clearContexts: () => {
    set({ contexts: [] });
  },

  getActiveContext: (activeContextId) => {
    if (!activeContextId) {
      return undefined;
    }

    return get().contexts.find((context) => context.id === activeContextId);
  },
}));
